// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import Loadable from 'react-loadable';
import Responsive from 'app/components/responsive/Responsive';
import Loader from 'app/components/commons/Loader';
import Page from 'app/containers/parts/Page';
import titleImage from 'app/images/jackpotSlots/title.png';
import titleImage2x from 'app/images/jackpotSlots/title_2x.png';
import leftCoins from 'app/images/jackpotSlots/left_coins.png';
import leftCoins2x from 'app/images/jackpotSlots/left_coins_2x.png';
import rightCoins from 'app/images/jackpotSlots/right_coins.png';
import rightCoins2x from 'app/images/jackpotSlots/right_coins_2x.png';
import * as jackpotActions from 'app/actions/jackpotSlotsActions';
import * as userActions from 'app/actions/userActions';
import t from 'app/utils/translate';
import { JACKPOT_SLOTS_POLL_DURATION } from 'app/constants/pollDurations';
import PhaserLoader from '../games/PhaserLoader';
import JackpotLegend from './jackpotSlots/JackpotLegend';
import FormValidationMessage from '../components/commons/FormValidationMessage';
import SignInLink from '../components/links/SignInLink';
import Button from '../components/commons/Button';

type Props = {
  errors: Array<Object>,
  getCurrentUser: () => void,
  getWinners: () => void,
  isPosting: boolean,
  isSignedIn: boolean,
  initialJackpotAmount: number,
  onPostSpin: () => void,
  resetJackpotSlots: () => void,
  spinsRemaining: ?number,
  stopSpinning: () => void,
  unlockTime: ?string,
  winAmount: number,
  winners: Array<Object>,
};

const JackpotSlotsWrapper = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 60px)',
  // justifyContent: 'space-between',
});

const ImgDiv = glamorous.div(({ theme, image1x, image2x }) => ({
  backgroundImage: `url(${image1x})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  margin: '0 auto auto',
  [theme.mediaQuery.pixelDensity2x]: {
    backgroundImage: `url(${image2x})`,
  },
}));

const LeftCoinsContainer = glamorous.div({
  position: 'absolute',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  display: 'flex',
});

const LeftCoins = glamorous(ImgDiv)({
  height: '40vw',
  maxHeight: 390,
  width: 238,
});

const RightCoins = glamorous(ImgDiv)(({ theme }) => ({
  height: '20vw',
  maxHeight: 235,
  width: 180,
  position: 'absolute',
  right: 0,
  pointerEvents: 'none',
  backgroundPosition: 'right top',
  [theme.mediaQuery.desktopAndUp]: {
    right: 50,
  },
}));

const EmptySideBar = glamorous.div({});

const TitleWrapper = glamorous.div(({ theme }) => ({
  margin: '0 auto',
  [theme.mediaQuery.desktopAndUp]: {
    marginBottom: theme.spacing.small,
  },
}));

const Title = glamorous.img({
  height: 'auto',
  width: '80vw',
  maxWidth: 500,
  zIndex: 10,
});

const JackpotSlotsGameLoadable = Loadable({
  loader: () => import(/* webpackChunkName: "jackpotslots" */ './jackpotSlots/JackpotSlotsGame'),
  loading: Loader,
});

const FinePrint = glamorous.span(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing.small,
  fontSize: theme.fontSize.xxsmall,
}));

const Center = glamorous.div({
  margin: '0 auto',
});

class ReactJackpotSlots extends React.Component<Props> {
  props: Props;

  timer: number = 0; // eslint-disable-line react/sort-comp

  componentDidMount() {
    const { getCurrentUser } = this.props;

    getCurrentUser();
    this.requestWinners();
  }

  requestWinners = () => {
    const { getWinners } = this.props;
    getWinners();
    // Poll for updates
    this.timer = setTimeout(this.requestWinners, JACKPOT_SLOTS_POLL_DURATION);
  };

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  render() {
    const {
      errors,
      getCurrentUser,
      isPosting,
      isSignedIn,
      initialJackpotAmount,
      onPostSpin,
      resetJackpotSlots,
      spinsRemaining,
      stopSpinning,
      unlockTime,
      winAmount,
      winners,
    } = this.props;

    return (
      <Page>
        <Responsive desktopLarge>
          <LeftCoinsContainer>
            <EmptySideBar />
            <LeftCoins image1x={leftCoins} image2x={leftCoins2x} />
          </LeftCoinsContainer>
          <RightCoins image1x={rightCoins} image2x={rightCoins2x} />
        </Responsive>
        <JackpotSlotsWrapper>
          <Responsive desktop mobilePortrait>
            <TitleWrapper>
              <Title src={titleImage} srcSet={`${titleImage} 1x, ${titleImage2x} 2x`} />
            </TitleWrapper>
          </Responsive>
          <Responsive desktop mobilePortrait>
            <JackpotLegend />
          </Responsive>
          {errors.length > 0 && <FormValidationMessage message={errors} />}
          {isSignedIn && (
            <PhaserLoader loader={Loader}>
              <Responsive desktop>
                {isDesktop => (
                  <JackpotSlotsGameLoadable
                    errors={errors}
                    getCurrentUser={getCurrentUser}
                    isDesktop={isDesktop}
                    isPosting={isPosting}
                    initialJackpotAmount={initialJackpotAmount}
                    loader={Loader}
                    onDestroy={resetJackpotSlots}
                    onPostSpin={onPostSpin}
                    parent="game-div"
                    spinsRemaining={spinsRemaining}
                    stopSpinning={stopSpinning}
                    unlockTime={unlockTime}
                    winAmount={winAmount}
                    winners={winners}
                  />
                )}
              </Responsive>
            </PhaserLoader>
          )}
          {!isSignedIn && (
            <Center>
              <SignInLink>
                <Button kind="primary" fake>
                  {t('Sign In')}
                </Button>
              </SignInLink>
            </Center>
          )}
          <Responsive desktop mobilePortrait>
            <FinePrint>
              Spin and Win pays out in Bonus PlayChips that can be used to enter into Platinum
              challenges. The JACKPOT will pay out in REAL PlayChips that can be directly cashed out
              at the PlayChip Fantasy store.
            </FinePrint>
          </Responsive>
        </JackpotSlotsWrapper>
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    errors: state.jackpotSlots.errors,
    isPosting: state.jackpotSlots.isPosting,
    isSignedIn: state.user.isSignedIn,
    initialJackpotAmount: state.jackpotSlots.initialJackpotAmount,
    spinsRemaining: state.jackpotSlots.spinsRemaining,
    unlockTime: state.jackpotSlots.unlockTime,
    winAmount: state.jackpotSlots.winAmount,
    winners: state.jackpotSlots.winners,
  };
}

const actions = {
  getCurrentUser: userActions.getCurrentUser,
  getWinners: jackpotActions.getWinners,
  onPostSpin: jackpotActions.postSpin,
  resetJackpotSlots: jackpotActions.resetJackpotSlots,
  stopSpinning: jackpotActions.stopSpinning,
};

export default connect(mapStateToProps, actions)(ReactJackpotSlots);
